"use client";

import {
  Upload,
  Globe2,
  Layout,
  QrCode,
  Smartphone,
  ChefHat,
  Compass,
  FileText,
  Sparkles,
} from "lucide-react";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";

export const FeatureSection = () => {
  const t = useTranslations("Features");
  const homeT = useTranslations("HomePage");

  const features = [
    {
      icon: <Upload className="w-10 h-10 text-primary" />,
      titleKey: "instantMenuDigitization",
      descriptionKey: "instantMenuDigitizationDesc",
    },
    {
      icon: <Globe2 className="w-10 h-10 text-primary" />,
      titleKey: "perfectCulinaryTranslations",
      descriptionKey: "perfectCulinaryTranslationsDesc",
    },
    {
      icon: <ChefHat className="w-10 h-10 text-primary" />,
      titleKey: "customizableMenuControl",
      descriptionKey: "customizableMenuControlDesc",
    },
    {
      icon: <QrCode className="w-10 h-10 text-primary" />,
      titleKey: "qrCodes",
      descriptionKey: "qrCodesDesc",
    },
    {
      icon: <Smartphone className="w-10 h-10 text-primary" />,
      titleKey: "mobileOptimized",
      descriptionKey: "mobileOptimizedDesc",
    },
    {
      icon: <Compass className="w-10 h-10 text-primary" />,
      titleKey: "websiteIntegration",
      descriptionKey: "websiteIntegrationDesc",
    },
    {
      icon: <Layout className="w-10 h-10 text-primary" />,
      titleKey: "centralizedManagement",
      descriptionKey: "centralizedManagementDesc",
    },
    {
      icon: <FileText className="w-10 h-10 text-primary" />,
      titleKey: "printReady",
      descriptionKey: "printReadyDesc",
    },
    {
      icon: <Sparkles className="w-10 h-10 text-primary" />,
      titleKey: "startFree",
      descriptionKey: "startFreeDesc",
    },
  ];

  return (
    <section
      id="features"
      className="container mx-auto px-4 py-16 md:py-24 lg:py-32 relative"
      aria-labelledby="features-heading"
    >
      {/* Background decorative elements */}
      <div
        className="absolute right-0 top-1/4 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -z-10 opacity-50"
        aria-hidden="true"
      ></div>
      <div
        className="absolute left-0 bottom-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl -z-10 opacity-40"
        aria-hidden="true"
      ></div>

      <div className="text-center mb-16">
        <h2
          id="features-heading"
          className="text-3xl md:text-4xl font-display font-[650] tracking-tight mb-4"
        >
          {homeT("expandCustomerBase")}
        </h2>
        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto font-sans">
          {homeT("breakLanguageBarriers")}
        </p>
        <div
          className="w-24 h-1 bg-gradient-to-r from-transparent via-primary/50 to-transparent mx-auto mt-8"
          aria-hidden="true"
        ></div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-[1200px] mx-auto">
        {features.map((feature, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
            className="group bg-menu-paper border border-border hover:border-primary/20 p-8 rounded-xl hover:shadow-md transition-all duration-300 relative overflow-hidden"
          >
            <div
              className="absolute inset-0 bg-gradient-to-b from-food-beige/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              aria-hidden="true"
            />
            <div className="relative z-10">
              <div
                className="p-3 bg-menu-accent/30 rounded-xl inline-block mb-4 group-hover:bg-menu-accent/50 transition-colors duration-300"
                aria-hidden="true"
              >
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold mb-3 font-display">
                {t(feature.titleKey)}
              </h3>
              <p className="text-muted-foreground font-sans">
                {t(feature.descriptionKey)}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default FeatureSection;
